import React, { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { Preview } from "@creatomate/preview";
import { makeGet, makePost } from "@culturehq/client";
import { ModalDialog } from "@culturehq/components";

import { Dimensions, ElementSource, MediaVersion, Story } from "chq";

import { StoriesStackProvider } from "app/context/StoriesStackContext";
import { useDoor, useFetch } from "app/hooks";
import { t } from "app/utils";

import ChqModalDialog from "components/common/ChqModalDialog";

import { useTemplateBuilder } from "components/admin/templates/context/TemplateBuilderContext";
import ModalTitle from "./header/ModalTitle";
import { CreatomateContext, CustomButtonType } from "./contexts/VideoStudioContext";
import PlayerOptions from "./header/ModalActions";
import Menu from "./sidebar/Menu";
import MenuTabs from "./sidebar/MenuTabs";
import CreatomatePlayer from "./player/CreatomatePlayer";
import WorkingAreaSeparator from "./WorkingAreaSeparator";
import Toolbar from "./player/toolbar/Toolbar";
import Timeline from "./timeline/Timeline";
import { PostStoryState } from "../post-stories-v2/types";
import TriggerButton from "./TriggerButton";
import StoryContext from "./contexts/StoryContext";
import { IMAGE_TYPE, VIDEO_TYPE } from "./utils/constants";
import { usePostStoryContext } from "../post-stories-v2/contexts/PostStoryContext";
import { needToBlockSubtitles } from "./sidebar/tabs/subtitle/utils";

const TIMELINE_INITIAL_HEIGHT = 336;
export const UNSAVED_MESSAGE = t("You have unsaved changes, are you sure you'd like to close the studio?");

type VideoStudioModalProps = {
  CustomButton?: CustomButtonType;
  dimensions?: Dimensions;
  disabled?: boolean;
  justIcon?: boolean;
  montage: boolean;
  onMediaEdited?: (value: ElementSource, thumbnail: PostStoryState["thumbnail"]) => void;
  onPauseVideo?: (value?: boolean) => void;
  previewRef: React.MutableRefObject<Preview | undefined>;
  stories?: Story[];
}

type VideoStudioModalOptionsProps = VideoStudioModalProps & {
  open: boolean;
  onOpenModal: () => void;
  onClose: () => void;
}

const VideoStudioModal: React.FC<VideoStudioModalOptionsProps> = ({
  CustomButton,
  dimensions,
  disabled,
  justIcon,
  montage,
  onMediaEdited,
  open,
  onOpenModal,
  onClose,
  onPauseVideo,
  previewRef
}) => {
  const {
    cancelAutosaving,
    data,
    handleUpdateData,
    restartData
  } = useContext(CreatomateContext);
  const {
    creatomateJson,
    mediaType,
    story,
    lastMediaVersion
  } = data;

  const { templateBuilderSelection } = useTemplateBuilder();
  const { restartContextValues } = useTemplateBuilder();
  const history = useHistory();
  const [draggablePositionY, setDraggablePositionY] = useState(0);

  const {
    state: { selectedVideos },
    setState
  } = usePostStoryContext();

  const { json: { templates } } = useFetch(
    () => {
      if (open) return makeGet("/creatomate_templates", { templateType: montage ? "montage" : mediaType });
      return Promise.resolve({ json: { templates: [] } });
    }, [open, montage, mediaType]
  );

  // validation to know if we need to block subtitles
  useEffect(() => {
    if (open) {
      handleUpdateData({
        blockSubtitles: needToBlockSubtitles(
          creatomateJson || {}, lastMediaVersion as MediaVersion
        )
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMediaVersion, open]);

  useEffect(
    () => {
      if (open) {
        if (!montage && story) {
          const height = creatomateJson?.height || story.media?.height || dimensions?.height || 0;
          const width = creatomateJson?.width || story.media?.width || dimensions?.width || 0;
          handleUpdateData({ aspectRatio: width / height });
        }
        if (templateBuilderSelection !== "none") {
          handleUpdateData({ mediaType: templateBuilderSelection === "image" ? IMAGE_TYPE : VIDEO_TYPE });
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]
  );

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => { // Added type annotation
      cancelAutosaving();
      const message = UNSAVED_MESSAGE;
      // eslint-disable-next-line no-param-reassign
      event.returnValue = message;
    };
    const handlePopState = (): boolean => {
      // eslint-disable-next-line no-alert
      const confirmation = window.confirm(UNSAVED_MESSAGE);
      if (confirmation) {
        return true;
      }
      return false;
    };
    const unblock = history.block((_: any, action: any): any => {
      if (action === "POP") return handlePopState();
      return true;
    });

    if (open) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      unblock();
    }
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      unblock();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, onClose, open]);

  const restartVideoCreator = () => {
    restartData();
    if (previewRef.current) previewRef.current.dispose();
    // eslint-disable-next-line no-param-reassign
    previewRef.current = undefined;
  };

  const onConfirmClose = useCallback(
    async () => {
      const message = UNSAVED_MESSAGE;

      // eslint-disable-next-line no-alert
      if (window.confirm(message)) {
        cancelAutosaving();
        restartContextValues();
        restartVideoCreator();
        if (selectedVideos) {
          if (selectedVideos.length > 1) {
            setState({
              selectedVideos: [],
              selectedMedia: undefined,
              selectedThumbnailUrl: undefined
            });
          }
        }
        onClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose]
  );

  const handleDraggablePositionYChange = (value: number) => {
    setDraggablePositionY(value);
  };

  const handleCloseModal = () => onClose();

  return (
    <>
      {open && (
        <ChqModalDialog entrance="zoomIn" className="stories-video-editor-modal Story-Trend video-studio-modal video-studio-modal-v2" ariaLabelledBy="video-studio-title" ariaDescribedby="video-studio-title">
          <ModalDialog.Heading>
            {previewRef.current && (
              <Fragment>
                <ModalTitle
                  preview={previewRef.current}
                />
                <PlayerOptions
                  onClose={handleCloseModal}
                  onConfirmClose={onConfirmClose}
                  open={open}
                  preview={previewRef.current}
                />
              </Fragment>
            )}
          </ModalDialog.Heading>
          <ModalDialog.Body className="p-0">
            <div className="Sidebar">
              <Menu
                mediaBeingEdited={!!onMediaEdited}
                preview={previewRef.current}
                templates={templates}
              />
            </div>
            <div className="WorkingArea">
              <div className="WorkingArea-Top" style={{ height: `calc(100vh - ${TIMELINE_INITIAL_HEIGHT - draggablePositionY}px)` }}>
                <div className="Stories col-sm-4 p-0">
                  {previewRef.current && (
                    <MenuTabs
                      preview={previewRef.current}
                      templates={templates}
                    />
                  )}
                </div>
                <CreatomatePlayer creatomateJson={creatomateJson} previewRef={previewRef} outputFormat={templateBuilderSelection === "image" ? "png" : "mp4"} />
              </div>
              {previewRef.current && (
                <WorkingAreaSeparator
                  draggablePositionY={draggablePositionY}
                  onDraggablePositionYChange={handleDraggablePositionYChange}
                  preview={previewRef.current}
                />
              )}
              <div className="Toolbar" style={{ top: `calc(100vh - ${TIMELINE_INITIAL_HEIGHT - draggablePositionY}px)` }}>
                <Toolbar preview={previewRef.current} />
                {previewRef.current && (
                  <Timeline
                    draggablePositionY={draggablePositionY}
                    preview={previewRef.current}
                    timelineHeight={TIMELINE_INITIAL_HEIGHT}
                  />
                )}
              </div>
            </div>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
      <TriggerButton
        CustomButton={CustomButton}
        disabled={disabled}
        justIcon={justIcon}
        onOpenModal={onOpenModal}
        onPauseVideo={onPauseVideo}
      />
    </>
  );
};

export default ({ montage, stories, ...rest }: VideoStudioModalProps) => {
  const { data: { onAnotherModalOpen, onThirdPartyMediasReload } } = useContext(CreatomateContext);
  const { open, onOpen: onOpenModal, onClose } = useDoor(false);
  const {
    state: { selectedVideos, selectedVideosChanged },
    setState
  } = usePostStoryContext();

  useEffect(() => {
    if (selectedVideos) {
      if (selectedVideos.length > 1 && selectedVideosChanged) {
        onOpenModal();
        setState({ selectedVideosChanged: false, pausedVideo: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideos]);

  useEffect(() => {
    if (open && onAnotherModalOpen) {
      onAnotherModalOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => {
    if (rest?.onPauseVideo) rest.onPauseVideo(false);
    if (onThirdPartyMediasReload) onThirdPartyMediasReload(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (montage) {
    return (
      <StoriesStackProvider isOpen={open} controlledStories={stories}>
        <StoryContext>
          <VideoStudioModal
            {...rest}
            montage={montage}
            onClose={onClose}
            onOpenModal={onOpenModal}
            open={open}
          />
        </StoryContext>
      </StoriesStackProvider>
    );
  }

  return (
    <VideoStudioModal
      {...rest}
      montage={montage}
      onClose={onClose}
      onOpenModal={onOpenModal}
      open={open}
    />
  );
};
